/**
 * Unwrapping a longitude series that has gaps in it.
 *
 * <h2>Why the existing unwrap cannot be used</h2>
 * `App.js`'s `unwrapDegrees` assumes every entry is a number. Hand it a null and the arithmetic
 * does not throw — it quietly produces nonsense: `null - previous` is NaN, so neither wrap branch
 * fires, and then `null + offset` coerces to the offset itself and is plotted as a real level.
 * The next iteration compares against that null too, so one gap corrupts the rest of the series
 * rather than just its own point.
 *
 * <p>ALL produces gaps as a matter of course. An exact opposition has no mean direction, so a
 * two-body average of Rahu and Ketu is undefined on every bar, and any selection can cancel out
 * on some of them. Those bars must break the line, not bend it.
 *
 * <h2>What happens across a gap</h2>
 * The running offset is CARRIED, and the comparison resumes against the last known value rather
 * than against the null. The bodies kept moving while the average was undefined; discarding the
 * offset would drop the line by a whole revolution the first time it resumed after a wrap.
 *
 * <p>A wrap that happens entirely inside a gap is unknowable — there is no observation to detect
 * it from — so the series can be one revolution out after a long gap. That is inherent to having
 * a gap at all, and it is why the comparison uses the last known value: it keeps the error to the
 * cases where it is genuinely undecidable rather than introducing it at every gap.
 */

/**
 * @param {Array<number|null|undefined>} degrees raw longitudes in [0, 360), with gaps
 * @returns {Array<number|null>} an unwrapped series, nulls preserved in place
 */
export function unwrapDegreesWithGaps(degrees) {
  if (!Array.isArray(degrees) || degrees.length === 0) return [];

  const out = new Array(degrees.length);
  let offset = 0;
  let lastRaw = null;

  for (let i = 0; i < degrees.length; i++) {
    const raw = degrees[i];
    if (!Number.isFinite(raw)) {
      // NaN and Infinity are treated as gaps too: a non-finite value cannot be plotted, and
      // letting one through would poison the offset for every point after it.
      out[i] = null;
      continue;
    }
    if (lastRaw !== null) {
      const diff = raw - lastRaw;
      if (diff < -180) {
        offset += 360;
      } else if (diff > 180) {
        offset -= 360;
      }
    }
    out[i] = raw + offset;
    lastRaw = raw;
  }
  return out;
}

export default unwrapDegreesWithGaps;
